import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Badge,
  Button,
  Field,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TemporaryPasswordPanel,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useResetOwnerPasswordMutation, useTransitionMerchantStatusMutation } from '../api';
import { merchantActions, type MerchantAction, type MerchantSummary } from '../types';

import { MerchantStatusBadge } from './MerchantStatusBadge';

import { ConfirmPasswordField } from '@/components/ui/ConfirmPasswordField';
import { useToast } from '@/components/ui/toast/ToastProvider';
import { HandOverSheet } from '@/features/handover/components/HandOverSheet';
import { adminName } from '@/features/portfolios/types';
import { useSession } from '@/lib/session';

type Dialog =
  | { readonly kind: 'closed' }
  | {
      readonly kind: 'transition';
      readonly merchant: MerchantSummary;
      readonly action: MerchantAction;
    }
  | { readonly kind: 'resetOwner'; readonly merchant: MerchantSummary }
  | {
      readonly kind: 'issued';
      readonly account: string;
      /** The owner's e-mail address, for the hand-over sheet (absent when the server did not say). */
      readonly email: string | null;
      readonly password: string;
      readonly expiresAt: string;
    };

const CLOSED: Dialog = { kind: 'closed' };

/**
 * One row per shop; the buttons offered depend on its status (`merchantActions`) and are a UX hint only - the API and, beneath it,
 * `app.transition_merchant_status()` decide what is legal.
 *
 * Two kinds of action, two levels of ceremony:
 *
 *   * approve / reactivate: a reason (10 characters at least, audited);
 *   * suspend / refuse: the reason AND the operator's own password again (decision of 8 October 2026 - the platform has no second
 *     factor, so a stolen or unattended session must not be enough to cut a business off or close the door on an applicant).
 *
 * A superadmin also gets « Mot de passe du propriétaire » on every shop that has not been refused: a new provisional password for the
 * owner, shown once. That button is not rendered for a platform_admin (display hint; the server refuses them anyway).
 */
/** Checkboxes of the superadmin's list (lot P3): the selected shops are assigned together. */
export interface MerchantSelection {
  readonly selected: ReadonlySet<string>;
  readonly toggle: (tenantId: string) => void;
  readonly toggleAll: (merchants: readonly MerchantSummary[]) => void;
}

export function MerchantTable({
  merchants,
  selection,
  onAssign,
}: {
  merchants: readonly MerchantSummary[];
  selection?: MerchantSelection | undefined;
  /** Superadmin only: opens the « Attribuer » dialog for one shop. */
  onAssign?: ((merchant: MerchantSummary) => void) | undefined;
}): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const { roles } = useSession();
  const isSuperadmin = roles.includes('superadmin');
  const transitionMutation = useTransitionMerchantStatusMutation();
  const resetMutation = useResetOwnerPasswordMutation();

  const [dialog, setDialog] = useState<Dialog>(CLOSED);
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  function open(next: Dialog): void {
    setFormError(null);
    setReason('');
    setPassword('');
    setDialog(next);
  }

  function close(): void {
    setReason('');
    setPassword('');
    setDialog(CLOSED);
  }

  async function onConfirmTransition(): Promise<void> {
    if (dialog.kind !== 'transition') return;
    setFormError(null);
    try {
      await transitionMutation.mutateAsync({
        tenantId: dialog.merchant.tenantId,
        status: dialog.action.target,
        reason,
        ...(dialog.action.askPassword ? { confirmPassword: password } : {}),
      });
      toast.success(t('admin.merchants.updatedToast'));
      close();
    } catch (error) {
      setFormError(t(resolveErrorMessageKey(error)));
    }
  }

  async function onConfirmReset(): Promise<void> {
    if (dialog.kind !== 'resetOwner') return;
    setFormError(null);
    try {
      const issued = await resetMutation.mutateAsync({
        tenantId: dialog.merchant.tenantId,
        reason,
        confirmPassword: password,
      });
      setReason('');
      setPassword('');
      setDialog({
        kind: 'issued',
        account: issued.ownerEmail ?? dialog.merchant.legalName ?? dialog.merchant.tenantId,
        email: issued.ownerEmail ?? null,
        password: issued.temporaryPassword,
        expiresAt: issued.expiresAt,
      });
    } catch (error) {
      setFormError(t(resolveErrorMessageKey(error)));
    }
  }

  const reasonValid = reason.trim().length >= 10;
  const needsPassword =
    (dialog.kind === 'transition' && dialog.action.askPassword) || dialog.kind === 'resetOwner';
  const canSubmit = reasonValid && (!needsPassword || password.length > 0);

  const transitionTitle =
    dialog.kind === 'transition'
      ? dialog.action.target === 'refused'
        ? t('admin.merchants.refuseModalTitle')
        : dialog.action.target === 'suspended'
          ? t('admin.merchants.suspendModalTitle')
          : t('admin.merchants.updateModalTitle')
      : '';

  return (
    <>
      <Table caption={t('admin.nav.merchants')}>
        <TableHead>
          <TableRow>
            {selection === undefined ? null : (
              <TableHeaderCell>
                <input
                  type="checkbox"
                  className="accent-accent"
                  aria-label={t('admin.merchants.selectAllAria')}
                  checked={
                    merchants.length > 0 &&
                    merchants.every((merchant) => selection.selected.has(merchant.tenantId))
                  }
                  onChange={() => {
                    selection.toggleAll(merchants);
                  }}
                />
              </TableHeaderCell>
            )}
            <TableHeaderCell>{t('admin.merchants.table.colLegalName')}</TableHeaderCell>
            {isSuperadmin ? (
              <TableHeaderCell>{t('admin.merchants.table.colManager')}</TableHeaderCell>
            ) : null}
            <TableHeaderCell>{t('merchant.orders.table.colStatus')}</TableHeaderCell>
            <TableHeaderCell>{t('admin.merchants.table.colCreatedAt')}</TableHeaderCell>
            <TableHeaderCell> </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {merchants.map((merchant) => (
            <TableRow key={merchant.tenantId}>
              {selection === undefined ? null : (
                <TableCell>
                  <input
                    type="checkbox"
                    className="accent-accent"
                    aria-label={t('admin.merchants.selectRowAria', {
                      name: merchant.legalName ?? merchant.tenantId,
                    })}
                    checked={selection.selected.has(merchant.tenantId)}
                    onChange={() => {
                      selection.toggle(merchant.tenantId);
                    }}
                  />
                </TableCell>
              )}
              <TableCell>
                <span className="text-sm text-ink">{merchant.legalName ?? '—'}</span>
                <p className="font-mono text-xs text-inkFaint">{merchant.tenantId}</p>
              </TableCell>
              {isSuperadmin ? (
                <TableCell>
                  {merchant.managedBy === null || merchant.managedBy === undefined ? (
                    <Badge tone="warn">{t('admin.merchants.unassigned')}</Badge>
                  ) : (
                    <span className="text-sm text-ink">{adminName(merchant.managedBy)}</span>
                  )}
                </TableCell>
              ) : null}
              <TableCell>
                <MerchantStatusBadge status={merchant.status} />
                {isSuperadmin && merchant.status === 'pending' ? (
                  <p className="mt-1 text-xs text-inkFaint">
                    {merchant.managedBy === null || merchant.managedBy === undefined
                      ? t('admin.merchants.unassignedHint')
                      : t('admin.merchants.pendingForManager')}
                  </p>
                ) : null}
              </TableCell>
              <TableCell>
                <span className="text-xs text-inkFaint">
                  {new Date(merchant.createdAt).toLocaleString('fr-TN')}
                </span>
              </TableCell>
              <TableCell align="end">
                <div className="flex flex-wrap justify-end gap-2">
                  {merchantActions(merchant.status, isSuperadmin).map((action) => (
                    <Button
                      key={action.target}
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        open({ kind: 'transition', merchant, action });
                      }}
                    >
                      {t(action.labelKey)}
                    </Button>
                  ))}
                  {onAssign !== undefined ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        onAssign(merchant);
                      }}
                    >
                      {merchant.managedBy === null || merchant.managedBy === undefined
                        ? t('admin.merchants.action.assign')
                        : t('admin.merchants.action.reassign')}
                    </Button>
                  ) : null}
                  {(isSuperadmin && merchant.status !== 'refused') ||
                  (!isSuperadmin &&
                    merchant.status !== 'refused' &&
                    merchant.ownerAwaitingFirstLogin === true) ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        open({ kind: 'resetOwner', merchant });
                      }}
                    >
                      {isSuperadmin
                        ? t('admin.merchants.action.resetOwner')
                        : t('admin.merchants.action.reissueUnused')}
                    </Button>
                  ) : null}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Modal
        open={dialog.kind === 'transition' || dialog.kind === 'resetOwner'}
        title={
          dialog.kind === 'resetOwner' ? t('admin.merchants.resetOwnerTitle') : transitionTitle
        }
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              {t('merchant.common.cancel')}
            </Button>
            <Button
              onClick={() => {
                void (dialog.kind === 'resetOwner' ? onConfirmReset() : onConfirmTransition());
              }}
              loading={transitionMutation.isPending || resetMutation.isPending}
              disabled={!canSubmit}
            >
              {dialog.kind === 'resetOwner'
                ? t('admin.merchants.resetOwnerGenerate')
                : t('merchant.common.confirm')}
            </Button>
          </>
        }
      >
        {formError !== null ? (
          <p
            role="alert"
            className="mb-3 rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical"
          >
            {formError}
          </p>
        ) : null}

        {dialog.kind === 'transition' && dialog.action.target === 'refused' ? (
          <p className="mb-3 rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical">
            {t('admin.merchants.refuseWarning')}
          </p>
        ) : null}
        {dialog.kind === 'transition' && dialog.action.target === 'suspended' ? (
          <p className="mb-3 rounded-md bg-surface2 px-3 py-2 text-sm text-inkSoft">
            {t('admin.merchants.suspendWarning')}
          </p>
        ) : null}
        {dialog.kind === 'resetOwner' ? (
          <p className="mb-3 rounded-md bg-surface2 px-3 py-2 text-sm text-inkSoft">
            {t('admin.merchants.resetOwnerWarning')}
            {isSuperadmin ? '' : ` ${t('admin.merchants.reissueUnusedWarning')}`}
          </p>
        ) : (
          <p className="mb-3 rounded-md bg-surface2 px-3 py-2 text-sm text-inkSoft">
            {t('admin.merchants.noEmailHint')}
          </p>
        )}

        <div className="flex flex-col gap-4">
          <Field label={t('admin.common.reasonLabel')}>
            {(bind) => (
              <textarea
                {...bind}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
                rows={3}
                maxLength={500}
                className="ecom-input"
              />
            )}
          </Field>

          {needsPassword ? <ConfirmPasswordField value={password} onChange={setPassword} /> : null}
        </div>
      </Modal>

      <Modal
        open={dialog.kind === 'issued'}
        title={t('admin.merchants.resetOwnerTitle')}
        onClose={close}
      >
        {dialog.kind === 'issued' ? (
          <div className="flex flex-col gap-4">
            <TemporaryPasswordPanel
              account={dialog.account}
              password={dialog.password}
              expiresAt={dialog.expiresAt}
              onDone={close}
              onExpire={() => {
                setDialog({ ...dialog, password: '' });
              }}
            />
            <HandOverSheet email={dialog.email} expiresAt={dialog.expiresAt} />
          </div>
        ) : null}
      </Modal>
    </>
  );
}
